// The program screen of a guided session: the gait under its clock, the timed
// rows of a step, and what "+1 min" and "Next gait" change. Pure, no '@/' import.
//
// The recording's own clock is never touched: the program screen reads a
// program clock and a program step, both adjusted by what the rider tapped.
// - "Next gait" moves the program clock forward by what is left of the gait:
//   an offset added to the recording's seconds.
// - "+1 min" lengthens the gait being ridden by one minute. Not a clock shift:
//   pulling the clock back a minute would, early in a gait, land back in the
//   previous one. The step is read with that gait one minute longer instead, so
//   everything after it moves by the same minute.

import { finite } from '../sessions/gait-total.js'

/** What one tap on "+1 min" adds to the current gait. */
export const EXTEND_GAIT_SECONDS = 60

/** A duration or a clock in seconds, as the program reads it: never negative, 0 for anything else. */
export const seconds = (value) => Math.max(0, finite(value))

/**
 * The timed rows of a step, as { gait, duration }: a named gait lasting more
 * than 0 s, standing included. The rows the phone guidance counts down and the
 * Record page line lists (MOVE PRO counts the step's own gaits down).
 */
export const timedRows = (gaits) =>
    (Array.isArray(gaits) ? gaits : [])
        .filter((row) => typeof row?.gait === 'string' && row.gait !== '' && seconds(row.duration) > 0)
        .map((row) => ({ gait: row.gait, duration: seconds(row.duration) }))

/**
 * The gait under the program clock, the one on screen: the first whose end is
 * still ahead, with the seconds left of it ({ index, remaining }). Null once
 * the step is over, or without a gait.
 */
export const gaitAt = (step, clock) => {
    const at = seconds(clock)
    const gaits = Array.isArray(step?.gaits) ? step.gaits : []
    let end = 0
    for (let index = 0; index < gaits.length; index++) {
        end += seconds(gaits[index]?.duration)
        if (at < end) return { index, remaining: end - at }
    }
    return null
}

/** Where the program stands: the recording's seconds, shifted by the offset. */
export const programElapsed = (liveDuration, offset) => finite(liveDuration) + finite(offset)

/**
 * "Next gait": the program clock jumps by what is left of the current gait,
 * landing exactly on the start of the next one. Never backwards.
 */
export const skipGaitOffset = (offset, remaining) => finite(offset) + Math.max(0, finite(remaining))

/**
 * "Next gait" only while a gait is running and another one follows: on the
 * last, skipping would end the program without its "finished" cue.
 */
export const canSkipGait = ({ remaining, hasNext }) => Boolean(hasNext) && finite(remaining) > 0

/** "+1 min" on the gait at `index`: the extra seconds per gait index, added up. */
export const extendGait = (extensions, index, added = EXTEND_GAIT_SECONDS) => {
    if (!Number.isInteger(index) || index < 0) return extensions ?? {}
    return { ...extensions, [index]: finite(extensions?.[index]) + added }
}

/**
 * The step as the rider now rides it: each extended gait longer by its extra
 * seconds, and the step's total with them. Untouched (same object) when nothing
 * was extended, so a session without a tap reads the step exactly as before.
 */
export const withExtensions = (step, extensions) => {
    const gaits = step?.gaits
    if (!Array.isArray(gaits)) return step
    const extra = (index) => finite(extensions?.[index])
    const total = gaits.reduce((sum, gait, index) => sum + extra(index), 0)
    if (!(total > 0)) return step
    return {
        ...step,
        duration: finite(step.duration) + total,
        gaits: gaits.map((gait, index) =>
            extra(index) > 0 ? { ...gait, duration: finite(gait.duration) + extra(index) } : gait
        ),
    }
}
